"use client";

import Image from "next/image";
import Link from "next/link";
import { ChevronRight, Star, Store } from "lucide-react";
import { useBrandsQuery } from "@/features/brands/hooks/useBrandsQuery";

export default function BrandsPage() {
  const { brands, loading } = useBrandsQuery();

  if (loading) {
    return (
      <div className="bg-[#F5F5F5] min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#FACC15] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-500">Loading brands...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#F5F5F5] min-h-screen">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-100 py-2">
        <div className="container mx-auto px-4 sm:px-6 flex items-center gap-2 text-[11px] font-medium text-gray-500">
          <Link href="/" className="hover:text-[#001F5F]">
            Home
          </Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-[#001F5F]">All Brands</span>
        </div>
      </div>

      {/* Header */}
      <div className="bg-[#001F5F] py-8">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-4">
            <div className="bg-[#FACC15] p-3 rounded-xl text-[#001F5F]">
              <Store className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-white">
                Shop by Brands
              </h1>
              <p className="text-white/80 text-sm mt-1">
                Explore products from your favorite brands
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4 mt-6 text-white/80 text-sm">
            <span>{brands.length} brands available</span>
          </div>
        </div>
      </div>

      {/* Brands Grid */}
      <div className="container mx-auto px-4 sm:px-6 py-8">
        {brands.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-100 p-12 text-center">
            <div className="text-4xl mb-4">🏪</div>
            <h3 className="text-lg font-bold text-[#001F5F] mb-2">
              No Brands Found
            </h3>
            <p className="text-sm text-gray-500">
              Check back later for new brands.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
            {brands.map((brand) => (
              <Link
                key={brand.id}
                href={`/brand/${brand.slug}`}
                className="bg-white rounded-xl border border-gray-100 p-6 hover:shadow-lg transition-all duration-300 group flex flex-col items-center text-center"
              >
                <div className="relative w-20 h-20 mb-4 rounded-full bg-white ring-1 ring-gray-200 flex items-center justify-center overflow-hidden group-hover:ring-gray-300 transition-colors">
                  {brand.logo_url ? (
                    <Image
                      src={brand.logo_url}
                      alt={brand.name}
                      fill
                      className="object-contain p-2"
                    />
                  ) : (
                    <span className="text-2xl font-bold text-gray-400 group-hover:text-[#001F5F] transition-colors">
                      {brand.name.slice(0, 2).toUpperCase()}
                    </span>
                  )}
                </div>

                <h3 className="font-bold text-[#001F5F] text-lg mb-1 group-hover:text-blue-600 transition-colors">
                  {brand.name}
                </h3>

                {parseFloat(brand.avg_rating) > 0 && (
                  <div className="flex items-center gap-1">
                    <div className="bg-[#001F5F] text-white text-[10px] px-1.5 py-0.5 rounded flex items-center gap-0.5 font-bold">
                      {parseFloat(brand.avg_rating).toFixed(1)}{" "}
                      <Star className="w-2.5 h-2.5 fill-white" />
                    </div>
                    <span className="text-[10px] text-gray-400">
                      ({brand.total_reviews})
                    </span>
                  </div>
                )}
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

